/**
 * MFAEnrollmentModal Component
 *
 * Full MFA enrollment flow with QR code display and TOTP verification.
 * Shared modal for MFA enrollment functionality.
 * Used by both regular users and admin users.
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';

// 2. Internal absolute imports
import {
  Button,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  useToastNotification
} from '@enfomentis/ui';
import { mfaService } from '@/services/MFAService';
import { useCountdown } from '@/hooks/useCountdown';
import { getTimeSyncLink, getTimeSyncInstructions } from '@/utils/timeSyncLinks';
import type { EnrollStartResponse, MFAErrorCode } from '@/types/mfa';

/**
 * Props for MFAEnrollmentModal
 */
export interface MFAEnrollmentModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
  onSuccess?: () => void;
  /** Number of factors already enrolled (to determine if this is first or additional) */
  existingFactorCount?: number;
}

/**
 * Enrollment state machine
 */
type EnrollmentState =
  | { status: 'idle' }
  | { status: 'loading_enrollment' }
  | { status: 'showing_qr'; data: EnrollStartResponse }
  | { status: 'verifying_code'; data: EnrollStartResponse }
  | { status: 'success' };

/**
 * MFA Enrollment Modal Component
 *
 * Features:
 * - QR code generation for authenticator apps
 * - Manual secret entry option
 * - TOTP code verification
 * - Success state with auto-close
 * - Clock skew detection and hints
 * - Rate limiting with countdown
 */
export const MFAEnrollmentModal: React.FC<MFAEnrollmentModalProps> = ({
  isOpen,
  onOpenChange,
  onSuccess,
  existingFactorCount = 0
}) => {
  const toast = useToastNotification();

  // Determine if this is first enrollment or additional
  const isFirstEnrollment = existingFactorCount === 0;

  // Enrollment state machine
  const [enrollmentState, setEnrollmentState] = useState<EnrollmentState>({ status: 'idle' });

  // Form state
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [failureCount, setFailureCount] = useState(0);
  const [retryAfter, setRetryAfter] = useState(0);
  const [secretCopied, setSecretCopied] = useState(false);

  // Countdown for rate limiting
  const countdown = useCountdown(retryAfter);

  // Start enrollment when modal opens
  useEffect(() => {
    if (isOpen && enrollmentState.status === 'idle') {
      startEnrollment();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start enrollment process
  const startEnrollment = async (): Promise<void> => {
    setEnrollmentState({ status: 'loading_enrollment' });
    setError(null);

    const result = await mfaService.enrollStart();

    if (!result.success) {
      setError(getErrorMessage(result.error.error_code, result.error.message));
      setEnrollmentState({ status: 'idle' });

      // Handle rate limiting
      if (result.error.retry_after) {
        setRetryAfter(result.error.retry_after);
      }

      return;
    }

    setEnrollmentState({ status: 'showing_qr', data: result.data });
  };

  // Verify enrollment code
  const verifyCode = async (): Promise<void> => {
    console.log('🔐 MFAEnrollmentModal: verifyCode called, state:', enrollmentState.status, 'code:', code);

    if (enrollmentState.status !== 'showing_qr') {
      console.log('⚠️ MFAEnrollmentModal: Wrong state, returning early');
      return;
    }

    // Validate code format
    if (!/^\d{6}$/.test(code)) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    console.log('🔐 MFAEnrollmentModal: Starting verification...');
    setEnrollmentState({ status: 'verifying_code', data: enrollmentState.data });
    setError(null);

    const result = await mfaService.enrollVerify(
      enrollmentState.data.factor_id,
      enrollmentState.data.challenge_id,
      code
    );

    console.log('🔐 MFAEnrollmentModal: Verification result:', result);

    if (!result.success) {
      // Increment failure count
      setFailureCount(prev => prev + 1);

      // Get error message
      const errorMsg = getErrorMessage(result.error.error_code, result.error.message);
      setError(errorMsg);

      // Handle rate limiting
      if (result.error.retry_after) {
        setRetryAfter(result.error.retry_after);
      }

      // Return to showing_qr state
      setEnrollmentState({ status: 'showing_qr', data: enrollmentState.data });
      return;
    }

    // Success!
    setEnrollmentState({ status: 'success' });

    // Auto-close after 2 seconds
    setTimeout(() => {
      onOpenChange();

      // Show success toast
      toast.showToast({
        title: 'MFA Enabled Successfully',
        description: 'Your account is now protected with two-factor authentication',
        color: 'success',
        timeout: 4000
      });

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
    }, 2000);
  };

  // Get context-aware error message
  const getErrorMessage = (errorCode: MFAErrorCode, defaultMessage: string): string => {
    switch (errorCode) {
      case 'NOT_REAUTHED' as MFAErrorCode:
        return 'Please re-enter your password to enable MFA';
      case 'ALREADY_ENROLLED' as MFAErrorCode:
        return 'You already have MFA enabled. Please disable it first to re-enroll.';
      case 'RATE_LIMITED' as MFAErrorCode:
        return 'Too many attempts. Please wait before trying again.';
      case 'INVALID_CODE' as MFAErrorCode:
        return 'Invalid authentication code. Please try again.';
      case 'CLOCK_SKEW' as MFAErrorCode:
        return 'Invalid code. Please check your device time.';
      case 'CHALLENGE_EXPIRED' as MFAErrorCode:
        return 'Your session expired. Please close and restart enrollment.';
      default:
        return defaultMessage;
    }
  };

  // Copy secret to clipboard
  const copySecret = async (): Promise<void> => {
    if (enrollmentState.status !== 'showing_qr') return;

    try {
      await navigator.clipboard.writeText(enrollmentState.data.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy secret:', err);
    }
  };

  // Reset form state
  const resetForm = useCallback(() => {
    setEnrollmentState({ status: 'idle' });
    setCode('');
    setError(null);
    setFailureCount(0);
    setRetryAfter(0);
    setSecretCopied(false);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    resetForm();
    onOpenChange();
  }, [resetForm, onOpenChange]);

  // Determine if showing clock skew hint
  const showClockSkewHint = failureCount >= 3 || error?.includes('device time');

  // Log telemetry when clock skew hint is shown
  useEffect(() => {
    if (showClockSkewHint && enrollmentState.status === 'showing_qr') {
      console.log('📊 [TELEMETRY] clock_skew_hint_shown', {
        failure_count: failureCount,
        trigger: error?.includes('device time') ? 'error_message' : 'failure_threshold'
      });
    }
  }, [showClockSkewHint, enrollmentState.status, failureCount, error]);

  // Get time sync link
  const timeSyncLink = getTimeSyncLink();
  const timeSyncInstructions = getTimeSyncInstructions();

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="lg"
      placement="center"
      backdrop="blur"
      scrollBehavior="inside"
      isDismissable={enrollmentState.status !== 'verifying_code'}
      isKeyboardDismissDisabled={enrollmentState.status === 'verifying_code'}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h3 className="text-xl font-semibold">
                {isFirstEnrollment
                  ? 'Set Up Two-Factor Authentication'
                  : 'Add Another Authenticator'}
              </h3>
              <p className="text-sm text-text-secondary font-normal">
                {isFirstEnrollment
                  ? 'Add an extra layer of security to your account'
                  : 'Set up an additional authenticator app for your account'}
              </p>
            </ModalHeader>

            <ModalBody>
              {/* Loading State */}
              {enrollmentState.status === 'loading_enrollment' && (
                <div className="text-center py-8">
                  <p className="text-text-secondary">Setting up MFA...</p>
                </div>
              )}

              {/* Success State */}
              {enrollmentState.status === 'success' && (
                <div className="text-center py-8">
                  <div className="text-success-600 text-6xl mb-4">✓</div>
                  <h4 className="text-lg font-medium text-text-primary mb-2">
                    MFA Enabled Successfully
                  </h4>
                  <p className="text-text-secondary">
                    Your account is now protected with two-factor authentication
                  </p>
                </div>
              )}

              {/* Enrollment Flow (showing_qr or verifying_code) */}
              {(enrollmentState.status === 'showing_qr' || enrollmentState.status === 'verifying_code') && (
                <div className="space-y-6">
                  {/* QR Code Display */}
                  <div className="flex flex-col items-center gap-4 py-6">
                    <img
                      src={enrollmentState.data.qr_code}
                      alt="MFA QR Code"
                      className="w-48 h-48 rounded-lg border border-default-200"
                    />
                    <p className="text-text-secondary text-sm text-center">
                      Scan this code with your authenticator app<br />
                      (Google Authenticator, Authy, 1Password, etc.)
                    </p>
                  </div>

                  {/* Manual Entry */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-text-primary">
                      Or enter this code manually:
                    </p>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={enrollmentState.data.secret}
                        isReadOnly
                        classNames={{
                          input: "text-base font-mono",
                        }}
                      />
                      <Button
                        isIconOnly
                        variant="flat"
                        onPress={copySecret}
                        aria-label="Copy secret"
                      >
                        {secretCopied ? (
                          <Check className="w-4 h-4 text-success" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Verification Code Input */}
                  <div className="space-y-2">
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      autoComplete="one-time-code"
                      label="Verification Code"
                      placeholder="000000"
                      value={code}
                      onValueChange={setCode}
                      description="Enter the 6-digit code from your authenticator app"
                      isInvalid={!!error}
                      errorMessage={error || undefined}
                      isDisabled={enrollmentState.status === 'verifying_code' || countdown > 0}
                      autoFocus
                      classNames={{
                        input: "text-base tracking-wider",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && code.length === 6) {
                          verifyCode();
                        }
                      }}
                    />
                  </div>

                  {/* Clock Skew Hint */}
                  {showClockSkewHint && (
                    <Alert color="warning" variant="flat">
                      <div className="space-y-2">
                        <p className="font-medium">Your device time may be incorrect</p>
                        <p className="text-sm">{timeSyncInstructions}</p>
                        {timeSyncLink && (
                          <a
                            href={timeSyncLink}
                            className="text-sm text-primary underline hover:text-primary-600"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open Time Settings
                          </a>
                        )}
                      </div>
                    </Alert>
                  )}

                  {/* Rate Limit Countdown */}
                  {countdown > 0 && (
                    <Alert color="danger" variant="flat">
                      <p>Too many attempts. Please try again in {countdown} seconds.</p>
                    </Alert>
                  )}
                </div>
              )}

              {/* Error State (when idle with error) */}
              {enrollmentState.status === 'idle' && error && (
                <Alert color="danger" variant="flat">
                  {error}
                </Alert>
              )}
            </ModalBody>

            {/* Footer (hide during success state) */}
            {enrollmentState.status !== 'success' && (
              <ModalFooter>
                <Button
                  color="default"
                  variant="light"
                  onPress={handleModalClose}
                  isDisabled={enrollmentState.status === 'verifying_code'}
                >
                  Cancel
                </Button>
                {(enrollmentState.status === 'showing_qr' || enrollmentState.status === 'verifying_code') && (
                  <Button
                    color="primary"
                    onPress={verifyCode}
                    isLoading={enrollmentState.status === 'verifying_code'}
                    isDisabled={code.length !== 6 || countdown > 0}
                  >
                    {enrollmentState.status === 'verifying_code' ? 'Verifying...' : 'Verify and Enable MFA'}
                  </Button>
                )}
                {enrollmentState.status === 'idle' && (
                  <Button
                    color="primary"
                    onPress={startEnrollment}
                    isDisabled={countdown > 0}
                  >
                    {countdown > 0 ? `Retry in ${countdown}s` : 'Try Again'}
                  </Button>
                )}
              </ModalFooter>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
